'use client'

import { useEffect, useImperativeHandle, useRef, type Ref } from 'react'

import { buildPerm, noise2D } from '@/lib/topo-noise'

export interface TopoFlowHandle {
  triggerRipple: (duration?: number) => void
  /** Where the terrain has drifted to, so a still frame can be pinned to the live one. */
  getPhase: () => number
}

interface TopoFlowProps {
  seed: number
  lineCount?: number
  speed?: number
  scale?: number
  strokeColour?: string
  strokeWidth?: number
  className?: string
  /** Draw one still frame instead of animating when the visitor prefers reduced motion. */
  respectReducedMotion?: boolean
  /** Draw one still frame at `phase`, whatever the visitor prefers and whether or not it is on screen. */
  freeze?: boolean
  /** Where the terrain starts its drift. */
  phase?: number
  /** A fixed pixel ratio, for rendering at an exact pixel size. */
  pixelRatio?: number
  ref?: Ref<TopoFlowHandle>
}

export function TopoFlow({
  seed,
  lineCount = 12,
  speed = 0.15,
  scale = 0.003,
  strokeColour = 'rgba(255,255,255,0.06)',
  strokeWidth = 1,
  className = '',
  respectReducedMotion = false,
  freeze = false,
  phase = 0,
  pixelRatio,
  ref,
}: TopoFlowProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rippleUntilRef = useRef(0)
  const rippleStartRef = useRef(0)
  const rippleDurationRef = useRef(400)
  const timeRef = useRef(phase)

  useImperativeHandle(ref, () => ({
    triggerRipple(duration = 400) {
      rippleStartRef.current = performance.now()
      rippleDurationRef.current = duration
      rippleUntilRef.current = performance.now() + duration
    },
    getPhase: () => timeRef.current,
  }))

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const perm = buildPerm(seed)
    const still =
      freeze ||
      (respectReducedMotion && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    let animId = 0
    let time = phase
    let lastFrame = performance.now()
    let isVisible = false

    function resize() {
      if (!canvas || !ctx) return
      // Cap DPR at 2 on small viewports so phones with 3x screens render
      // ~44% fewer pixels per frame without visibly losing line crispness.
      const dprCap = canvas.offsetWidth < 768 ? 2 : 3
      const dpr = pixelRatio ?? Math.min(devicePixelRatio || 1, dprCap)
      canvas.width = canvas.offsetWidth * dpr
      canvas.height = canvas.offsetHeight * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      // Resizing clears the canvas, and a still frame has no loop to redraw it.
      if (still && (isVisible || freeze)) animId = requestAnimationFrame(draw)
    }

    // Defer animation until the canvas scrolls into view
    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          isVisible = true
          lastFrame = performance.now()
          animId = requestAnimationFrame(draw)
        } else if (!entry.isIntersecting && isVisible) {
          isVisible = false
          cancelAnimationFrame(animId)
        }
      },
      { rootMargin: '200px' }
    )
    visibilityObserver.observe(canvas)

    // Marching squares: generate a noise heightmap grid, then trace
    // isolines at evenly spaced thresholds to produce true contour lines
    // that curve around hills and valleys like a real topographic map.

    const isPhone = canvas.offsetWidth < 480
    const isMobile = canvas.offsetWidth < 768
    const cellSize = isPhone ? 18 : isMobile ? 14 : 8
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t
    let frameSkip = 0

    function draw(now: number) {
      if (!canvas || !ctx) return

      // Mobile: skip every other frame for ~30fps
      if (isMobile) {
        frameSkip++
        if (frameSkip % 2 !== 0) {
          animId = requestAnimationFrame(draw)
          return
        }
      }

      const dt = now - lastFrame
      lastFrame = now

      // Ripple: briefly accelerate the terrain drift then settle back.
      // This makes contours flow faster during the hero text transition
      // without reshaping the terrain (which caused visual jumps).
      let speedMult = 1
      if (now < rippleUntilRef.current) {
        const elapsed = now - rippleStartRef.current
        const t = Math.min(elapsed / rippleDurationRef.current, 1)
        speedMult = 1 + 2 * Math.sin(t * Math.PI)
      }
      if (!still) time += speed * (dt / 16.67) * speedMult
      timeRef.current = time

      const cssW = canvas.offsetWidth
      const cssH = canvas.offsetHeight
      ctx.clearRect(0, 0, cssW, cssH)

      const cols = Math.ceil(cssW / cellSize) + 1
      const rows = Math.ceil(cssH / cellSize) + 1
      const timeOffset = time * 0.002

      // Build heightmap grid from noise
      const grid = new Float32Array(cols * rows)
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const nx = col * cellSize * scale
          const ny = row * cellSize * scale + timeOffset
          grid[row * cols + col] = noise2D(perm, nx, ny)
        }
      }

      // Distribute threshold levels evenly across the noise range
      ctx.strokeStyle = strokeColour
      ctx.lineWidth = strokeWidth
      ctx.beginPath()

      for (let level = 0; level < lineCount; level++) {
        // Thresholds from -0.6 to 0.6 (central range of noise output)
        const threshold = -0.6 + (1.2 * (level + 1)) / (lineCount + 1)

        // March through each cell and draw contour segments
        for (let row = 0; row < rows - 1; row++) {
          for (let col = 0; col < cols - 1; col++) {
            const tl = grid[row * cols + col]
            const tr = grid[row * cols + col + 1]
            const br = grid[(row + 1) * cols + col + 1]
            const bl = grid[(row + 1) * cols + col]

            // Classify corners: 1 if above threshold, 0 if below
            const config =
              (tl >= threshold ? 8 : 0) |
              (tr >= threshold ? 4 : 0) |
              (br >= threshold ? 2 : 0) |
              (bl >= threshold ? 1 : 0)

            // Skip empty (0) and full (15) cells
            if (config === 0 || config === 15) continue

            const x = col * cellSize
            const y = row * cellSize

            // Interpolated edge positions
            const top = lerp(x, x + cellSize, (threshold - tl) / (tr - tl))
            const right = lerp(y, y + cellSize, (threshold - tr) / (br - tr))
            const bottom = lerp(x, x + cellSize, (threshold - bl) / (br - bl))
            const left = lerp(y, y + cellSize, (threshold - tl) / (bl - tl))

            // Draw line segments for each marching squares case
            switch (config) {
              case 1:
              case 14:
                ctx.moveTo(x, left)
                ctx.lineTo(bottom, y + cellSize)
                break
              case 2:
              case 13:
                ctx.moveTo(bottom, y + cellSize)
                ctx.lineTo(x + cellSize, right)
                break
              case 3:
              case 12:
                ctx.moveTo(x, left)
                ctx.lineTo(x + cellSize, right)
                break
              case 4:
              case 11:
                ctx.moveTo(top, y)
                ctx.lineTo(x + cellSize, right)
                break
              case 5:
                ctx.moveTo(x, left)
                ctx.lineTo(top, y)
                ctx.moveTo(bottom, y + cellSize)
                ctx.lineTo(x + cellSize, right)
                break
              case 6:
              case 9:
                ctx.moveTo(top, y)
                ctx.lineTo(bottom, y + cellSize)
                break
              case 7:
              case 8:
                ctx.moveTo(x, left)
                ctx.lineTo(top, y)
                break
              case 10:
                ctx.moveTo(x, left)
                ctx.lineTo(bottom, y + cellSize)
                ctx.moveTo(top, y)
                ctx.lineTo(x + cellSize, right)
                break
            }
          }
        }
      }

      ctx.stroke()
      if (isVisible && !still) animId = requestAnimationFrame(draw)
    }

    resize()
    const resizeObserver = new ResizeObserver(resize)
    resizeObserver.observe(canvas)

    return () => {
      cancelAnimationFrame(animId)
      resizeObserver.disconnect()
      visibilityObserver.disconnect()
    }
  }, [
    seed,
    lineCount,
    speed,
    scale,
    strokeColour,
    strokeWidth,
    respectReducedMotion,
    freeze,
    phase,
    pixelRatio,
  ])

  return (
    <canvas
      ref={canvasRef}
      className={`pointer-events-none absolute inset-0 z-[3] h-full w-full ${className}`}
      aria-hidden="true"
    />
  )
}
